// src/pages/mikrotik/PoolsPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Database,
  Edit3,
  Filter,
  Gauge,
  Globe2,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Router as RouterIcon,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface Pool {
  id?: string;
  name: string;
  ranges: string;
  total: number;
  used: number;
  available: number;
}

type RouterId = string | number;

interface RouterPools {
  router_id: RouterId;
  router_name: string;
  router_ip?: string;
  pools: Pool[];
}

interface PoolForm {
  name: string;
  ranges: string;
}

interface FlattenedPool extends Pool {
  routerId: RouterId;
  routerName: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const getPoolPercentUsed = (pool: Pool) =>
  Number(pool.total || 0) > 0
    ? Math.round((Number(pool.used || 0) / Number(pool.total || 0)) * 100)
    : 0;

const utilizationTone = (percent: number) => {
  if (percent >= 90) return "bg-rose-500";
  if (percent >= 70) return "bg-orange-500";
  if (percent >= 40) return "bg-amber-400";
  return "bg-emerald-500";
};

const utilizationBadgeTone = (percent: number) => {
  if (percent >= 90) return "border-rose-200 bg-rose-100 text-rose-700";
  if (percent >= 70) return "border-orange-200 bg-orange-100 text-orange-800";
  if (percent >= 40) return "border-amber-200 bg-amber-100 text-amber-800";
  return "border-emerald-200 bg-emerald-100 text-emerald-800";
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading IP pools…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while pool usage is being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const UtilizationBar: React.FC<{ pool: Pool }> = ({ pool }) => {
  const percent = getPoolPercentUsed(pool);

  return (
    <div className="min-w-[160px]">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] font-black text-slate-600">
          {percent}% used
        </span>

        <span
          className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${utilizationBadgeTone(
            percent,
          )}`}
        >
          {pool.used || 0}/{pool.total || 0}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${utilizationTone(percent)}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 7 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-36"
              : index === 2
                ? "w-56"
                : index === 5
                  ? "w-40"
                  : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-40 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const PoolsPage: React.FC = () => {
  const { showToast } = useToast();

  const [routerPools, setRouterPools] = useState<RouterPools[]>([]);
  const [loadingPools, setLoadingPools] = useState(false);

  const [selectedRouterId, setSelectedRouterId] = useState<string>("all");
  const [formRouterId, setFormRouterId] = useState<string>("");
  const [search, setSearch] = useState("");

  const [form, setForm] = useState<PoolForm>({
    name: "",
    ranges: "",
  });

  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingRouterId, setEditingRouterId] = useState<RouterId | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    routerId: RouterId;
    name: string;
  } | null>(null);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showError = (msg: string) => {
    setError(msg);
    setSuccess(null);
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const fetchPools = async () => {
    try {
      setLoadingPools(true);
      resetMessages();

      const data = await fetchApp<RouterPools[]>("/pool/");
      const list = Array.isArray(data) ? data : [];

      setRouterPools(list);

      setFormRouterId((current) => {
        if (
          current &&
          list.some((router) => String(router.router_id) === String(current))
        ) {
          return current;
        }

        return list[0]?.router_id ? String(list[0].router_id) : "";
      });
    } catch (err: any) {
      const msg = err?.message || "Failed to load pools";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setLoadingPools(false);
    }
  };

  useEffect(() => {
    fetchPools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allPools = useMemo<FlattenedPool[]>(() => {
    return routerPools.flatMap((router) =>
      (router.pools || []).map((pool) => ({
        ...pool,
        routerId: router.router_id,
        routerName: router.router_name,
      })),
    );
  }, [routerPools]);

  const filteredPools = useMemo(() => {
    const q = search.trim().toLowerCase();

    return allPools.filter((pool) => {
      const matchesRouter =
        selectedRouterId === "all" ||
        String(pool.routerId) === String(selectedRouterId);

      if (!matchesRouter) return false;

      if (!q) return true;

      return [pool.name, pool.ranges, pool.routerName]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [allPools, search, selectedRouterId]);

  const stats = useMemo(() => {
    const totalRouters = routerPools.length;
    const totalPools = allPools.length;
    const totalIps = allPools.reduce(
      (sum, pool) => sum + Number(pool.total || 0),
      0,
    );
    const usedIps = allPools.reduce(
      (sum, pool) => sum + Number(pool.used || 0),
      0,
    );
    const availableIps = allPools.reduce(
      (sum, pool) => sum + Number(pool.available || 0),
      0,
    );
    const percentUsed =
      totalIps > 0 ? Math.round((usedIps / totalIps) * 100) : 0;
    const highUsagePools = allPools.filter(
      (pool) => getPoolPercentUsed(pool) >= 80,
    ).length;

    return {
      totalRouters,
      totalPools,
      totalIps,
      usedIps,
      availableIps,
      percentUsed,
      highUsagePools,
    };
  }, [routerPools, allPools]);

  const selectedFormRouter = useMemo(() => {
    return (
      routerPools.find(
        (router) => String(router.router_id) === String(formRouterId),
      ) || null
    );
  }, [routerPools, formRouterId]);

  const handleCreateOrUpdatePool = async (e: React.FormEvent) => {
    e.preventDefault();
    resetMessages();

    if (!form.name.trim() || !form.ranges.trim()) {
      const msg = "Pool name and ranges are required";
      showError(msg);
      showToast(msg, "error");
      return;
    }

    const targetRouterId = editingRouterId ?? formRouterId;

    if (!targetRouterId) {
      const msg = "Please select a router first";
      showError(msg);
      showToast(msg, "error");
      return;
    }

    try {
      setSaving(true);

      const body = {
        name: form.name.trim(),
        ranges: form.ranges.trim(),
      };

      const method = editingName ? "PUT" : "POST";
      const url = editingName
        ? `/pool/${encodeURIComponent(editingName)}?router_id=${targetRouterId}`
        : `/pool?router_id=${targetRouterId}`;

      const res = await fetchApp<any>(url, {
        method,
        body: JSON.stringify(body),
      });

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to save pool");
      }

      const msg = editingName
        ? "Pool updated successfully"
        : "Pool created successfully";

      showSuccess(msg);
      showToast(msg, "success");

      setForm({ name: "", ranges: "" });
      setEditingName(null);
      setEditingRouterId(null);
      await fetchPools();
    } catch (err: any) {
      const msg = err?.message || "Failed to save pool";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPool = (routerId: RouterId, pool: Pool) => {
    resetMessages();

    setEditingName(pool.name);
    setEditingRouterId(routerId);
    setFormRouterId(String(routerId));
    setForm({
      name: pool.name,
      ranges: pool.ranges,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditingRouterId(null);
    setForm({ name: "", ranges: "" });
  };

  const askDeletePool = (routerId: RouterId, name: string) => {
    setPendingDelete({ routerId, name });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setConfirmLoading(true);
      resetMessages();

      const res = await fetchApp<any>(
        `/pool/${encodeURIComponent(pendingDelete.name)}?router_id=${
          pendingDelete.routerId
        }`,
        {
          method: "DELETE",
        },
      );

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to delete pool");
      }

      const msg = "Pool deleted successfully";
      showSuccess(msg);
      showToast(msg, "success");

      await fetchPools();
    } catch (err: any) {
      const msg = err?.message || "Failed to delete pool";
      showError(msg);
      showToast(msg, "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const closeAlert = () => {
    setError(null);
    setSuccess(null);
  };

  if (loadingPools && routerPools.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  MikroTik IP Pool Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  IP Pools
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Manage address pools, monitor usage, and keep track of
                  available IP capacity across your routers.
                </p>
              </div>

              <button
                type="button"
                onClick={fetchPools}
                disabled={loadingPools || saving}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {loadingPools ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {loadingPools ? "Refreshing…" : "Refresh Pools"}
              </button>
            </div>
          </div>
        </div>

        {loadingPools && routerPools.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing IP pools…
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={closeAlert}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Routers"
            value={stats.totalRouters}
            sub="Routers with pools"
            icon={<RouterIcon className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Pools"
            value={stats.totalPools}
            sub="Total configured pools"
            icon={<Layers className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Total IPs"
            value={stats.totalIps}
            sub="Combined capacity"
            icon={<Globe2 className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Available"
            value={stats.availableIps}
            sub="Free addresses"
            icon={<Database className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Used"
            value={`${stats.percentUsed}%`}
            sub={`${stats.usedIps} addresses in use`}
            icon={<Gauge className="h-5 w-5" />}
            tone={stats.percentUsed >= 90 ? "rose" : "emerald"}
          />
        </div>

        {/* Create / Edit form */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                {editingName ? (
                  <Edit3 className="h-5 w-5" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  {editingName ? "Edit IP Pool" : "Create IP Pool"}
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {editingName
                    ? `Editing ${editingName}`
                    : "Select a router, then add the pool name and ranges."}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleCreateOrUpdatePool} className="p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[260px_1fr_1fr_auto] lg:items-end">
              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Router
                </label>

                <select
                  value={formRouterId}
                  onChange={(e) => setFormRouterId(e.target.value)}
                  disabled={saving || Boolean(editingName)}
                  className={`${inputCls} cursor-pointer`}
                >
                  <option value="">Select router...</option>
                  {routerPools.map((router) => (
                    <option
                      key={String(router.router_id)}
                      value={String(router.router_id)}
                    >
                      {router.router_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Pool Name
                </label>

                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. customers_pool"
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Ranges
                </label>

                <input
                  value={form.ranges}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, ranges: e.target.value }))
                  }
                  placeholder="e.g. 10.10.1.2-10.10.1.254"
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving || !formRouterId}
                  className={`${btnBase} h-10 flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600 lg:flex-none`}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : editingName ? (
                    <Save className="h-4 w-4" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {saving ? "Saving…" : editingName ? "Update" : "Create"}
                </button>

                {editingName && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {selectedFormRouter && (
              <p className="mt-3 text-[12px] font-medium text-slate-500">
                Target router:{" "}
                <span className="font-black text-slate-800">
                  {selectedFormRouter.router_name}
                </span>
              </p>
            )}
          </form>
        </div>

        {/* Pools list */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Layers className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Pool List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Showing {filteredPools.length} of {allPools.length} pools
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search pool, range, router..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-72">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={selectedRouterId}
                  onChange={(e) => setSelectedRouterId(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Routers</option>
                  {routerPools.map((router) => (
                    <option
                      key={String(router.router_id)}
                      value={String(router.router_id)}
                    >
                      {router.router_name}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {(search || selectedRouterId !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setSelectedRouterId("all");
                  }}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Pool</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">Ranges</th>
                  <th className="px-5 py-3.5 text-right font-black">Total</th>
                  <th className="px-5 py-3.5 text-right font-black">
                    Available
                  </th>
                  <th className="px-5 py-3.5 font-black">Utilization</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loadingPools ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredPools.length > 0 ? (
                  filteredPools.map((pool) => (
                    <tr
                      key={`${pool.routerId}-${pool.name}`}
                      className="transition-colors hover:bg-emerald-50/60"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <Layers className="h-4 w-4" />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-black text-slate-900">
                              {pool.name}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              {getPoolPercentUsed(pool)}% used
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          <RouterIcon className="h-3 w-3" />
                          {pool.routerName}
                        </span>
                      </td>

                      <td className="max-w-[360px] px-5 py-4">
                        <p
                          className="truncate font-mono text-[12px] font-bold text-slate-700"
                          title={pool.ranges}
                        >
                          {pool.ranges || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-slate-800">
                        {pool.total || 0}
                      </td>

                      <td className="px-5 py-4 text-right font-mono text-[12.5px] font-black text-emerald-700">
                        {pool.available || 0}
                      </td>

                      <td className="px-5 py-4">
                        <UtilizationBar pool={pool} />
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditPool(pool.routerId, pool)}
                            className={`${btnBase} h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              askDeletePool(pool.routerId, pool.name)
                            }
                            className={`${btnBase} h-9 border border-rose-200 bg-rose-100 px-3 text-rose-700 hover:bg-rose-200`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Layers className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No pools found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No IP pools match your current filters.
                      </p>

                      {(search || selectedRouterId !== "all") && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearch("");
                            setSelectedRouterId("all");
                          }}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loadingPools ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredPools.length > 0 ? (
              filteredPools.map((pool) => (
                <div
                  key={`${pool.routerId}-${pool.name}`}
                  className="px-4 py-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-black text-slate-900">
                        {pool.name}
                      </p>

                      <p className="mt-0.5 truncate font-mono text-[11.5px] font-bold text-slate-500">
                        {pool.ranges || "No range"}
                      </p>
                    </div>

                    <span
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-black ${utilizationBadgeTone(
                        getPoolPercentUsed(pool),
                      )}`}
                    >
                      {getPoolPercentUsed(pool)}%
                    </span>
                  </div>

                  <div className="mt-3">
                    <UtilizationBar pool={pool} />
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Total
                      </p>

                      <p className="mt-1 font-mono text-[13px] font-black text-slate-900">
                        {pool.total || 0}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                        Used
                      </p>

                      <p className="mt-1 font-mono text-[13px] font-black text-slate-900">
                        {pool.used || 0}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        Free
                      </p>

                      <p className="mt-1 font-mono text-[13px] font-black text-slate-900">
                        {pool.available || 0}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <RouterIcon className="h-3 w-3" />
                      {pool.routerName}
                    </span>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditPool(pool.routerId, pool)}
                        className={`${btnBase} h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => askDeletePool(pool.routerId, pool.name)}
                        className={`${btnBase} h-9 border border-rose-200 bg-rose-100 px-3 text-rose-700 hover:bg-rose-200`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Layers className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No pools found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No IP pools match your current filters.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredPools.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {allPools.length}
              </span>{" "}
              pools
            </p>

            {stats.highUsagePools > 0 && (
              <p className="text-[12px] font-bold text-amber-700">
                {stats.highUsagePools} pool
                {stats.highUsagePools === 1 ? "" : "s"} above 80% usage
              </p>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        title="Delete IP pool?"
        description={
          pendingDelete
            ? `The pool “${pendingDelete.name}” will be removed from the selected router. This action cannot be undone.`
            : "This pool will be removed. This action cannot be undone."
        }
        confirmLabel="Delete pool"
        cancelLabel="Cancel"
        variant="danger"
        onCancel={() => {
          if (confirmLoading) return;
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default PoolsPage;
